/**
 * Quote Page Functionality
 * Handles quote page display and enquiry modal
 */

class QuotePage extends HTMLElement {
  constructor() {
    super();
    this.quoteItems = [];
    this.modal = null;
  }

  connectedCallback() {
    this.loadQuoteItems();
    this.renderQuoteItems();
    this.setupRemoveButtons();
    this.setupQuantityInputs();

    // Listen for quote updates from other pages
    document.addEventListener('quote:updated', () => {
      this.loadQuoteItems();
      this.renderQuoteItems();
    });
  }

  loadQuoteItems() {
    if (typeof window.QuoteUtils !== 'undefined') {
      this.quoteItems = window.QuoteUtils.getQuoteItems();
    } else {
      try {
        const items = localStorage.getItem('quote_items');
        this.quoteItems = items ? JSON.parse(items) : [];
      } catch (e) {
        this.quoteItems = [];
      }
    }
  }

  renderQuoteItems() {
    // Try multiple selectors to find the tbody container
    const container = this.querySelector('tbody') ||
      this.querySelector('.quote-items-container') ||
      this.querySelector('.js-contents');
    if (!container) {
      return;
    }

    if (this.quoteItems.length === 0) {
      container.innerHTML = `
        <tr class="quote-empty">
          <td colspan="5" class="text-center p-40">
            <h2 class="h4 mb-15">Your quote is empty</h2>
            <a href="${window.Shopify?.routes?.root_url || '/'}" class="btn btn-primary">Continue Shopping</a>
          </td>
        </tr>
      `;
      return;
    }

    let html = '';

    this.quoteItems.forEach((item, index) => {
      html += `
        <tr class="quote-item" data-variant-id="${item.variantId}">
          <td class="quote-item__media border-0 px-0 flex align-center gap-10 border-bottom py-30">
            <button type="button" class="quote-remove-btn inline-flex align-center" data-variant-id="${item.variantId}" aria-label="Remove ${item.title}">
              <svg width="14" height="14" fill="none" class="dark-grey hover-heading-color transition">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M10.5 4.25h-7v7.588c0 .073.014.146.041.219a.727.727 0 0 0 .137.191.416.416 0 0 0 .177.123c.073.027.15.041.233.041h5.824c.082 0 .16-.014.232-.04a.416.416 0 0 0 .178-.124.53.53 0 0 0 .123-.191.485.485 0 0 0 .055-.22V4.25Zm-.588-1.162h2.338c.164 0 .3.06.41.178a.52.52 0 0 1 .178.396c0 .164-.06.305-.178.424a.557.557 0 0 1-.41.164h-.588v7.588c0 .237-.046.465-.137.684-.09.21-.214.391-.369.546a1.615 1.615 0 0 1-.56.383c-.21.091-.438.137-.684.137H4.088c-.246 0-.474-.05-.684-.15a1.931 1.931 0 0 1-.56-.37 1.755 1.755 0 0 1-.37-.546 1.76 1.76 0 0 1-.136-.684V4.25H1.75a.603.603 0 0 1-.424-.164.603.603 0 0 1-.164-.424.54.54 0 0 1 .164-.396.579.579 0 0 1 .424-.178h2.338V2.5c0-.237.045-.46.137-.67.09-.219.214-.406.369-.56.155-.165.337-.292.547-.383.218-.091.45-.137.697-.137h2.324c.246 0 .474.046.684.137.21.09.396.218.56.383.155.154.278.341.37.56.09.21.136.433.136.67v.588Zm-4.662 0h3.5V2.5a.433.433 0 0 0-.055-.219.531.531 0 0 0-.123-.191.416.416 0 0 0-.177-.123.513.513 0 0 0-.233-.055H5.838a.513.513 0 0 0-.233.055.416.416 0 0 0-.177.123.73.73 0 0 0-.137.191.55.55 0 0 0-.041.219v.588Z" fill="currentColor"/>
              </svg>
            </button>
            <a href="${item.url || '#'}" class="quote-item__link fs-0 rounded-5 relative overflow-hidden block" style="width: 10rem; aspect-ratio: 3/4;">
              ${item.image ? `<img src="${item.image}" alt="${item.title || 'Product'}" loading="lazy" class="w-full h-full object-cover">` : '<div class="w-full h-full bg-grey"></div>'}
            </a>
          </td>
          <td class="quote-item__details px-0 border-0 border-bottom py-30 pe-10">
            <div class="quote-item-info">
              <a href="${item.url || '#'}" class="product-item__name unset heading-style no-underline block lh-normal pe-20 word-break">
                ${item.title || 'Product'}
              </a>
            </div>
          </td>
          <td class="quote-item__quantity border-0 border-bottom py-30 px-10">
            <div class="quote-item__quantity-wrapper">
              <quantity-input class="quantity btn-rounded border inline-flex text-center grey-bg overflow-hidden">
                <button type="button" name="minus" class="quantity__button pointer border-0 w-40 grey-bg ps-20" data-variant-id="${item.variantId}" data-action="decrease">
                  <svg width="11" height="12" viewBox="0 0 11 2" fill="none">
                    <path d="M11 0.5L11 1.5L-4.37114e-08 1.5L0 0.5L11 0.5Z" fill="currentColor"></path>
                  </svg>
                </button>
                <input
                  type="number"
                  class="quantity-input bg-unset text-center appearance-none p-0-important w-38"
                  value="${item.quantity || 1}"
                  min="1"
                  data-variant-id="${item.variantId}"
                  style="--input-padding: 0;--inputs-border-width: 0;--input-height: 43px;--input-border-radius: 0;--input-bg: transparent"
                >
                <button type="button" name="plus" class="quantity__button pointer border-0 w-40 grey-bg pe-20" data-variant-id="${item.variantId}" data-action="increase">
                  <svg width="11" height="12" viewBox="0 0 11 12" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M5 11.5H6L6 6.5H11V5.5H6L6 0.5H5L5 5.5H0V6.5H5L5 11.5Z" fill="currentColor"></path>
                  </svg>
                </button>
              </quantity-input>
            </div>
          </td>
        </tr>
      `;
    });

    // Update container
    if (container.tagName === 'TBODY') {
      container.innerHTML = html;
    } else {
      container.innerHTML = `<table class="quote-items w-full border-0"><tbody>${html}</tbody></table>`;
    }
  }

  setupRemoveButtons() {
    this.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('.quote-remove-btn');
      if (removeBtn) {
        const variantId = removeBtn.dataset.variantId;
        this.removeItem(variantId);
      }
    });
  }

  setupQuantityInputs() {
    // Listen to change events on quantity inputs (QuantityInput will handle button clicks)
    this.addEventListener('change', (e) => {
      if (e.target.matches('.quantity-input[data-variant-id]')) {
        const variantId = e.target.dataset.variantId;
        const qty = Math.max(1, parseInt(e.target.value) || 1);
        this.updateQuantity(variantId, qty);
      }
    });
  }

  removeItem(variantId) {
    const variantIdNum = parseInt(variantId);
    if (typeof window.QuoteUtils !== 'undefined') {
      window.QuoteUtils.removeFromQuote(variantIdNum);
    } else {
      const items = this.quoteItems.filter(item => parseInt(item.variantId) !== variantIdNum);
      try {
        localStorage.setItem('quote_items', JSON.stringify(items));
      } catch (e) {
        // Error saving to localStorage
      }
    }

    this.loadQuoteItems();
    this.renderQuoteItems();

    if (typeof window.QuoteUtils !== 'undefined') {
      window.QuoteUtils.updateQuoteCount();
    }
  }

  updateQuantity(variantId, quantity) {
    const variantIdNum = parseInt(variantId);
    if (typeof window.QuoteUtils !== 'undefined') {
      window.QuoteUtils.updateQuantity(variantIdNum, quantity);
    } else {
      const item = this.quoteItems.find(i => parseInt(i.variantId) === variantIdNum);
      if (item) {
        item.quantity = quantity;
        try {
          localStorage.setItem('quote_items', JSON.stringify(this.quoteItems));
        } catch (e) {
          // Error saving to localStorage
        }
      }
    }

    this.loadQuoteItems();
    this.renderQuoteItems();

    if (typeof window.QuoteUtils !== 'undefined') {
      window.QuoteUtils.updateQuoteCount();
    }
  }

  formatPrice(price) {
    const priceNum = typeof price === 'string' ? parseFloat(price) : price;
    const priceInCents = Math.round(priceNum || 0);

    if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
      const moneyFormat = window.cartStrings?.money_format || window.themeGlobalVariables?.settings?.money_format;
      if (moneyFormat) {
        try {
          return window.Shopify.formatMoney(priceInCents, moneyFormat);
        } catch (e) {
          // Fall through to simple formatting
        }
      }
    }

    const priceFloat = (priceInCents / 100).toFixed(2);
    return `$${priceFloat}`;
  }
}

// Register custom element only if not already registered
if (!customElements.get('quote-page')) {
  customElements.define('quote-page', QuotePage);
}

/**
 * ============================================
 * QUICK ENQUIRY MODAL - Standalone handler
 * Works independently from the QuotePage element
 * ============================================
 */
(function () {
  'use strict';


  function getQuoteItems() {
    try {
      if (typeof window.QuoteUtils !== 'undefined') {
        return window.QuoteUtils.getQuoteItems() || [];
      }
      const items = localStorage.getItem('quote_items');
      return items ? JSON.parse(items) : [];
    } catch (e) {
      return [];
    }
  }

  function buildItemsList(items) {
    if (!items || items.length === 0) return 'No items in quote.';
    return items.map(item => {
      return `• ${item.title || 'Product'} — Qty: ${item.quantity || 1}`;
    }).join('\n');
  }

  function openEnquiryModal() {

    const items = getQuoteItems();

    // Build items list for the hidden field
    const itemsText = buildItemsList(items);

    // Check for existing modal overlay
    let overlay = document.getElementById('quick-enquiry-overlay');
    if (overlay) {
      // Update the items text
      const itemsField = overlay.querySelector('#qe-items-list');
      if (itemsField) itemsField.value = itemsText;
      overlay.style.display = 'flex';
      document.body.style.overflow = 'hidden';
      return;
    }


    // Create the modal overlay
    overlay = document.createElement('div');
    overlay.id = 'quick-enquiry-overlay';
    overlay.innerHTML = `
      <div class="qe-modal-backdrop"></div>
      <div class="qe-modal-container">
        <button type="button" class="qe-modal-close" aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12.5 3.5L3.5 12.5M3.5 3.5L12.5 12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
        <div class="qe-modal-body">
          <h3 class="qe-modal-title">Quick Enquiry</h3>
          <p class="qe-modal-subtitle">Fill in your details below and we'll get back to you with a quote.</p>

          ${items.length > 0 ? `
          <div class="qe-items-summary">
            <strong>Your Items (${items.length}):</strong>
            <ul class="qe-items-list">
              ${items.map(item => `<li>${item.title || 'Product'} — Qty: ${item.quantity || 1}</li>`).join('')}
            </ul>
          </div>
          ` : ''}

          <form action="/contact" method="post" id="QuoteEnquiryForm" class="qe-form">
            <input type="hidden" name="form_type" value="contact">
            <input type="hidden" name="utf8" value="✓">

            <div class="qe-field">
              <label for="qe-name">Full Name <span class="qe-required">*</span></label>
              <input type="text" id="qe-name" name="contact[name]" placeholder="Enter your full name" required autocomplete="name">
            </div>

            <div class="qe-field">
              <label for="qe-email">Email Address <span class="qe-required">*</span></label>
              <input type="email" id="qe-email" name="contact[email]" placeholder="Enter your email address" required autocomplete="email">
            </div>

            <div class="qe-field">
              <label for="qe-phone">Phone Number <span class="qe-required">*</span></label>
              <input type="tel" id="qe-phone" name="contact[phone]" placeholder="Enter your phone number" required autocomplete="tel">
            </div>

            <div class="qe-field">
              <label for="qe-company">Company Name</label>
              <input type="text" id="qe-company" name="contact[company]" placeholder="Enter your company name (optional)" autocomplete="organization">
            </div>

            <div class="qe-field">
              <label for="qe-message">Additional Message</label>
              <textarea id="qe-message" name="contact[note]" rows="3" placeholder="Any additional requirements or questions..."></textarea>
            </div>

            <!-- Hidden fields for Shopify contact form -->
            <input type="hidden" name="contact[subject]" value="Quote Enquiry from Cart">
            <textarea name="contact[body]" id="qe-items-list" style="display:none;">${itemsText}</textarea>

            <div class="qe-error-msg" id="qe-error-msg" style="display:none;"></div>

            <button type="submit" class="qe-submit-btn" id="qe-submit-btn">
              <span class="qe-submit-text">Send Enquiry</span>
              <span class="qe-submit-loading" style="display:none;">Sending...</span>
            </button>
          </form>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    // Add styles
    addModalStyles();

    // Show the modal
    overlay.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    // Close button handler
    overlay.querySelector('.qe-modal-close').addEventListener('click', closeEnquiryModal);

    // Backdrop click handler
    overlay.querySelector('.qe-modal-backdrop').addEventListener('click', closeEnquiryModal);

    // ESC key handler
    document.addEventListener('keydown', handleEscKey);

    // Form submit handler
    const form = overlay.querySelector('#QuoteEnquiryForm');
    form.addEventListener('submit', handleFormSubmit);

  }

  function closeEnquiryModal() {
    const overlay = document.getElementById('quick-enquiry-overlay');
    if (overlay) {
      overlay.style.display = 'none';
      document.body.style.overflow = '';
    }
    document.removeEventListener('keydown', handleEscKey);
  }

  function handleEscKey(e) {
    if (e.key === 'Escape') {
      closeEnquiryModal();
    }
  }

  function handleFormSubmit(e) {
    e.preventDefault();

    const form = e.target;
    const submitBtn = form.querySelector('#qe-submit-btn');
    const submitText = form.querySelector('.qe-submit-text');
    const submitLoading = form.querySelector('.qe-submit-loading');
    const errorMsg = form.querySelector('#qe-error-msg');

    // Validate required fields
    const name = form.querySelector('#qe-name').value.trim();
    const email = form.querySelector('#qe-email').value.trim();
    const phone = form.querySelector('#qe-phone').value.trim();

    if (!name || !email || !phone) {
      errorMsg.textContent = 'Please fill in all required fields.';
      errorMsg.style.display = 'block';
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      errorMsg.textContent = 'Please enter a valid email address.';
      errorMsg.style.display = 'block';
      return;
    }

    // Hide error, show loading
    errorMsg.style.display = 'none';
    submitBtn.disabled = true;
    submitText.style.display = 'none';
    submitLoading.style.display = 'inline';

    // Build the full body content for admin email
    const items = getQuoteItems();
    const company = form.querySelector('#qe-company').value.trim();
    const message = form.querySelector('#qe-message').value.trim();

    let bodyContent = `--- QUOTE ENQUIRY ---\n\n`;
    bodyContent += `Name: ${name}\n`;
    bodyContent += `Email: ${email}\n`;
    bodyContent += `Phone: ${phone}\n`;
    if (company) bodyContent += `Company: ${company}\n`;
    bodyContent += `\n--- REQUESTED ITEMS ---\n\n`;
    bodyContent += buildItemsList(items);
    if (message) bodyContent += `\n\n--- ADDITIONAL NOTES ---\n\n${message}`;
    bodyContent += `\n\n--- END OF ENQUIRY ---`;

    // Update the hidden body field
    const bodyField = form.querySelector('#qe-items-list');
    if (bodyField) bodyField.value = bodyContent;

    // Submit via fetch to Shopify contact endpoint
    const formData = new FormData();
    formData.append('form_type', 'contact');
    formData.append('utf8', '✓');
    formData.append('contact[name]', name);
    formData.append('contact[email]', email);
    formData.append('contact[phone]', phone);
    formData.append('contact[subject]', 'Quote Enquiry from Cart');
    formData.append('contact[body]', bodyContent);


    fetch('/contact#contact_form', {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'text/html,application/xhtml+xml'
      }
    })
      .then(response => {
        if (response.ok || response.status === 302 || response.status === 200) {
          // Success!
          showSuccessState(form);
        } else {
          throw new Error('Form submission failed with status: ' + response.status);
        }
      })
      .catch(error => {
        errorMsg.textContent = 'Something went wrong. Please try again or contact us directly.';
        errorMsg.style.display = 'block';
        submitBtn.disabled = false;
        submitText.style.display = 'inline';
        submitLoading.style.display = 'none';
      });
  }

  function showSuccessState(form) {
    const modalBody = form.closest('.qe-modal-body');
    if (modalBody) {
      modalBody.innerHTML = `
        <div class="qe-success">
          <div class="qe-success-icon">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <circle cx="32" cy="32" r="30" stroke="currentColor" stroke-width="2"/>
              <path d="M20 32L28 40L44 24" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h3 class="qe-modal-title">Enquiry Sent Successfully!</h3>
          <p class="qe-modal-subtitle">Thank you for your enquiry. Our team will review your request and get back to you shortly with a quote.</p>
          <button type="button" class="qe-submit-btn" onclick="document.getElementById('quick-enquiry-overlay').style.display='none'; document.body.style.overflow='';">
            Continue Browsing
          </button>
        </div>
      `;
    }
  }

  function addModalStyles() {
    if (document.getElementById('qe-modal-styles')) return;

    const style = document.createElement('style');
    style.id = 'qe-modal-styles';
    style.textContent = `
      #quick-enquiry-overlay {
        display: none;
        position: fixed;
        inset: 0;
        z-index: 99999;
        align-items: center;
        justify-content: center;
        padding: 20px;
      }
      .qe-modal-backdrop {
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.55);
        backdrop-filter: blur(4px);
        -webkit-backdrop-filter: blur(4px);
      }
      .qe-modal-container {
        position: relative;
        background: var(--color-background, #fff);
        border-radius: var(--rounded-radius, 12px);
        max-width: 520px;
        width: 100%;
        max-height: 90vh;
        overflow-y: auto;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        animation: qeSlideUp 0.3s ease-out;
      }
      @keyframes qeSlideUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .qe-modal-close {
        position: absolute;
        top: 16px;
        right: 16px;
        background: none;
        border: 1px solid var(--color-border, #e5e5e5);
        border-radius: 50%;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: var(--color-heading, #111);
        transition: all 0.2s;
        z-index: 1;
      }
      .qe-modal-close:hover {
        background: var(--grey-color, #f5f5f5);
        transform: rotate(90deg);
      }
      .qe-modal-body {
        padding: 36px 32px 32px;
      }
      .qe-modal-title {
        font-size: calc(var(--heading-font-scale, 1) * 2.2rem);
        font-weight: var(--heading-weight, 600);
        color: var(--color-heading, #111);
        margin: 0 0 8px 0;
        letter-spacing: var(--heading-letter-spacing, -0.034em);
      }
      .qe-modal-subtitle {
        color: var(--color-text, #666);
        font-size: 14px;
        margin: 0 0 24px 0;
        line-height: 1.5;
      }
      .qe-items-summary {
        background: var(--grey-color, #f5f5f5);
        border-radius: var(--rounded-radius, 8px);
        padding: 16px;
        margin-bottom: 24px;
        font-size: 13px;
        color: var(--color-text, #666);
      }
      .qe-items-summary strong {
        color: var(--color-heading, #111);
        display: block;
        margin-bottom: 8px;
        font-size: 14px;
      }
      .qe-items-list {
        list-style: none;
        padding: 0;
        margin: 0;
      }
      .qe-items-list li {
        padding: 4px 0;
        border-bottom: 1px solid var(--color-border, #e5e5e5);
      }
      .qe-items-list li:last-child {
        border-bottom: none;
      }
      .qe-form {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }
      .qe-field label {
        display: block;
        font-size: 13px;
        font-weight: 500;
        color: var(--color-heading, #111);
        margin-bottom: 6px;
      }
      .qe-required {
        color: var(--color-primary, #d0473e);
      }
      .qe-field input,
      .qe-field textarea {
        width: 100%;
        padding: var(--input-padding, 12px 16px);
        border: var(--inputs-border-width, 1px) solid var(--color-border, #e5e5e5);
        border-radius: var(--input-border-radius, var(--btn-radius, 8px));
        background: var(--input-bg, rgba(255,255,255,0.6));
        color: var(--color-heading, #111);
        font-size: var(--input-font-size, 15px);
        font-family: inherit;
        transition: border-color 0.2s;
        box-sizing: border-box;
      }
      .qe-field input:focus,
      .qe-field textarea:focus {
        outline: none;
        border-color: var(--color-heading, #111);
      }
      .qe-field input::placeholder,
      .qe-field textarea::placeholder {
        color: var(--input-placeholder-color, rgba(17,17,17,0.4));
      }
      .qe-field textarea {
        resize: vertical;
        min-height: 80px;
      }
      .qe-error-msg {
        background: #fef2f2;
        color: #dc2626;
        padding: 10px 14px;
        border-radius: var(--rounded-radius, 8px);
        font-size: 13px;
        border: 1px solid #fca5a5;
      }
      .qe-submit-btn {
        width: 100%;
        padding: var(--btn-padding-y, 14px) var(--btn-padding-x, 24px);
        background: var(--btn-primary-bg-color, #111);
        color: var(--btn-primary-color, #fff);
        border: none;
        border-radius: var(--btn-radius, 8px);
        font-size: var(--btn-font-size, 15px);
        font-weight: 600;
        font-family: inherit;
        cursor: pointer;
        text-transform: var(--btn-text-transform, none);
        letter-spacing: var(--btn-letter-spacing, 0);
        transition: all 0.3s;
        margin-top: 4px;
      }
      .qe-submit-btn:hover {
        background: var(--btn-primary-hover-bg-color, #333);
        color: var(--btn-primary-hover-color, #fff);
      }
      .qe-submit-btn:disabled {
        opacity: 0.7;
        cursor: not-allowed;
      }
      .qe-success {
        text-align: center;
        padding: 20px 0;
      }
      .qe-success-icon {
        color: #22c55e;
        margin-bottom: 20px;
      }
      .qe-success .qe-modal-title {
        margin-bottom: 12px;
      }
      .qe-success .qe-modal-subtitle {
        margin-bottom: 28px;
      }
      @media (max-width: 575px) {
        .qe-modal-body {
          padding: 28px 20px 24px;
        }
        .qe-modal-container {
          max-height: 95vh;
        }
      }
    `;
    document.head.appendChild(style);
  }

  // Expose globally so it can be called from anywhere
  window.openQuickEnquiryModal = openEnquiryModal;
  window.closeQuickEnquiryModal = closeEnquiryModal;

  // Auto-bind the Quick Enquiry button on cart page
  function initQuickEnquiryButton() {

    if (!document.body.classList.contains('template-cart')) {
      return;
    }

    // Look for the Quick Enquiry button
    const btn = document.querySelector('.quick-enquiry-btn') ||
      document.querySelector('#checkout') ||
      document.querySelector('.cart__checkout-button');


    if (btn) {
      // Remove any existing click handlers by cloning
      const newBtn = btn.cloneNode(true);
      if (btn.parentNode) {
        btn.parentNode.replaceChild(newBtn, btn);
      }

      newBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        openEnquiryModal();
      });

    } else {
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuickEnquiryButton);
  } else {
    // DOM already loaded
    initQuickEnquiryButton();
  }

  // Also try after a delay as a fallback (deferred scripts)
  setTimeout(initQuickEnquiryButton, 1000);

})();
